import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {MenuItem, Box, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React, {FC} from "react"
import {useAllDict} from "@/store/dict.c";
import {useDialogModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue} from "@/helper/method";
import {useCreateSiteAccount} from "@/service/siteAccount.s";
import {DictPO} from "@/model/system";

const validationSchema = z.object({
            id: z.unknown().default(0),
            link: z.string({message: "网址"}),
            username: z.string({message: "请填写账号"}),
            password: z.string({message: "请填写登录密码"}),
            siteNameId: z.number({message: "请选择网站名称"}),
            customerId: z.number().optional().default(0),
        }
    )
;

export type WriteSiteAccountForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

interface WriteSiteAccountProps {
    customerId: number
    options: DictPO[]
}

export type WriteSiteAccountType = {
    onSearch: (params: any) => void
    title: string
    options: DictPO[]
}

export const WriteSiteAccount: FC<WriteSiteAccountProps> = ({customerId}) => {
    const allDict = useAllDict()
    const {siteName} = allDict
    const {setToast} = useGlobalActions()
    const {mutateAsync: createSiteAccountMutate, isPending: createSiteAccountPending} = useCreateSiteAccount()
    const {setDialogModal} = useGlobalActions()
    const {id: dialogId, onSearch} = useDialogModal()
    const {
        register,
        control,
        handleSubmit,
        setValue,
        reset,
        formState: {errors},
    } = useForm<WriteSiteAccountForm>({
        resolver: zodResolver(validationSchema),
        values: {} as WriteSiteAccountForm,
        defaultValues,
    });

    const onSubmit = async (values: WriteSiteAccountForm) => {
        const result = await createSiteAccountMutate({...values, customerId})
        if (result.code == 0) {
            setDialogModal({open: false})
            onSearch && onSearch()
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <TextField value={value} onChange={event => onChange(event.target.value)}
                                              placeholder={"选择网站名称"} select={true}>
                                {siteName?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }} name={`siteNameId`}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <TextField
                            {...register("link")}
                            error={!!errors?.link}
                            helperText={errors?.link?.message}
                            placeholder="网址"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <TextField
                            {...register("username")}
                            error={!!errors?.username}
                            helperText={errors?.username?.message}
                            placeholder="账号"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <TextField
                            {...register("password")}
                            error={!!errors?.password}
                            helperText={errors?.password?.message}
                            placeholder="密码"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createSiteAccountPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
